import { getHours } from 'date-fns';
import type {
  DailySummary,
  DietEntry,
  MealSlot,
  Nutrients,
  Portion,
  RecentItem,
} from '@diet/types';

/** 餐別的顯示順序，等同一天的時間順序 */
export const MEAL_SLOTS: readonly MealSlot[] = [
  'breakfast',
  'lunch',
  'dinner',
  'snack',
];

export const MEAL_SLOT_LABELS: Record<MealSlot, string> = {
  breakfast: '早餐',
  lunch: '午餐',
  dinner: '晚餐',
  snack: '點心',
};

/**
 * 依當下時刻預選餐別，落在三餐時段外的一律歸為點心。
 *
 * 只用於新增時的預設值 —— 紀錄本身不存時間，餐別由使用者確認（多數情況下不必動）。
 * 收 `now` 而非自己讀時鐘，讓呼叫端與測試能指定當下是幾點。
 */
export function currentSlot(now: Date = new Date()): MealSlot {
  const hour = getHours(now);
  if (hour >= 5 && hour < 11) return 'breakfast';
  if (hour >= 11 && hour < 15) return 'lunch';
  if (hour >= 17 && hour < 22) return 'dinner';
  return 'snack';
}

/**
 * 取當日紀錄。
 *
 * 不排序：傳入的順序即後端的建立順序，正是同一餐之內該有的先後。
 */
export function selectEntriesByDate(
  entries: DietEntry[],
  dateKey: string,
): DietEntry[] {
  return entries.filter((entry) => entry.date === dateKey);
}

/**
 * 有紀錄的日期集合，供日期選擇面板標記。
 *
 * 回傳 Set 而非陣列：月曆會對顯示中的每一格逐一查詢，用陣列就是每格一次線性掃描。
 */
export function selectLoggedDates(entries: DietEntry[]): Set<string> {
  return new Set(entries.map((entry) => entry.date));
}

/**
 * Atwater 係數：每公克營養素對應的熱量。
 *
 * 用於 macro bar 的分段比例 —— 以**熱量占比**而非重量占比呈現，因為脂肪 1g 是 9 kcal、
 * 另外兩者是 4 kcal，按重量畫會讓脂肪的影響看起來只有實際的一半。
 */
const KCAL_PER_GRAM = { protein: 4, fat: 9, carbs: 4 } as const;

/** 三項巨量營養素是否至少有一項有值。 */
export function hasMacros(nutrients: Nutrients): boolean {
  return (
    nutrients.proteinG !== null ||
    nutrients.fatG !== null ||
    nutrients.carbsG !== null
  );
}

/**
 * 三大營養素各自的熱量占比（0–1），供 macro bar 分段。
 *
 * 未填的項目以 0 計入分母 —— 這裡算的是「已知的部分怎麼分佈」，不是補完缺漏。
 * 三項全空時回傳 null，呼叫端據此決定不渲染，而不是畫一條空條。
 */
export function macroShares(
  nutrients: Nutrients,
): { protein: number; fat: number; carbs: number } | null {
  const protein = (nutrients.proteinG ?? 0) * KCAL_PER_GRAM.protein;
  const fat = (nutrients.fatG ?? 0) * KCAL_PER_GRAM.fat;
  const carbs = (nutrients.carbsG ?? 0) * KCAL_PER_GRAM.carbs;

  const total = protein + fat + carbs;
  if (total <= 0) return null;

  return { protein: protein / total, fat: fat / total, carbs: carbs / total };
}

/** 單日加總。空清單回傳全 0，讓 UI 不需另外處理 undefined。 */
export function summarize(entries: DietEntry[]): DailySummary {
  const bySlot: Record<MealSlot, number> = {
    breakfast: 0,
    lunch: 0,
    dinner: 0,
    snack: 0,
  };

  let total = 0;
  const macros = { proteinG: 0, fatG: 0, carbsG: 0 };
  let missingMacroCount = 0;

  for (const entry of entries) {
    bySlot[entry.slot] += entry.calories;
    total += entry.calories;

    /* null 一律跳過，不當 0 加 —— 「不知道」與「確實不含」是兩件事 */
    macros.proteinG += entry.nutrients.proteinG ?? 0;
    macros.fatG += entry.nutrients.fatG ?? 0;
    macros.carbsG += entry.nutrients.carbsG ?? 0;
    if (!hasMacros(entry.nutrients)) missingMacroCount += 1;
  }

  return {
    total,
    bySlot,
    count: entries.length,
    macros,
    missingMacroCount,
  };
}

export interface MealGroupData {
  slot: MealSlot;
  entries: DietEntry[];
  subtotal: number;
}

/** 依餐別分組；沒有紀錄的餐別不出現在結果中 */
export function groupBySlot(entries: DietEntry[]): MealGroupData[] {
  return MEAL_SLOTS.map((slot) => {
    const slotEntries = entries.filter((entry) => entry.slot === slot);
    return {
      slot,
      entries: slotEntries,
      subtotal: slotEntries.reduce((sum, entry) => sum + entry.calories, 0),
    };
  }).filter((group) => group.entries.length > 0);
}

/**
 * 取某一餐別最近輸入過的品項，供表單一鍵帶入。
 *
 * 候選限定同一餐別——早餐與晚餐吃的東西通常是兩組，混在一起會讓十個位置大半是
 * 這一餐用不到的品項。同名品項只留最近的一次，熱量也取那一次的值：同品項的份量
 * 會變，最近一次通常最接近現況。
 *
 * `dateKey`（表單當下編輯的那天）當日該餐別已經記過的品項會被排除——那些就在
 * 下方的清單裡看得到，再列一次只是佔位置，點了還會記成重複的一筆。
 *
 * 「最近」以 `entries` 的順序判定（後端依日期、建立時間排序，本地新增的附在尾端），
 * 而不是比對日期字串：同一天記的多筆之間，日期分不出先後。
 */
export function selectRecentItems(
  entries: DietEntry[],
  slot: MealSlot,
  dateKey: string,
  limit: number,
): RecentItem[] {
  const taken = new Set<string>();
  for (const entry of entries) {
    if (entry.date === dateKey && entry.slot === slot) taken.add(entry.name);
  }

  const latest = new Map<string, { item: RecentItem; at: number }>();

  entries.forEach((entry, index) => {
    if (entry.slot !== slot || taken.has(entry.name)) return;

    /* 後面的一定比前面的新，故直接覆蓋；索引同時是排序用的「多近」 */
    latest.set(entry.name, {
      item: {
        name: entry.name,
        calories: entry.calories,
        nutrients: entry.nutrients,
        portion: entry.portion,
      },
      at: index,
    });
  });

  return [...latest.values()]
    .sort((a, b) => b.at - a.at)
    .slice(0, limit)
    .map(({ item }) => item);
}

/** 千分位格式化，供大字總計與清單數字共用 */
export function formatCalories(calories: number): string {
  return calories.toLocaleString('zh-TW');
}

/**
 * 份量的顯示文字。**「1 份」回傳 null** —— 那是預設值，沒有資訊量，
 * 每一列都掛著它只會讓真正有意義的份量（2 份、200 公克/毫升）失去對比。
 */
export function formatPortion(portion: Portion): string | null {
  if (portion.unit === 'serving') {
    return portion.quantity === 1 ? null : `${formatGrams(portion.quantity)} 份`;
  }
  /* 「公克/毫升」而非「公克」：同一個單位同時承載固體與液體，寫死其中一個會讓半數紀錄看起來是錯的 */
  return `${formatGrams(portion.quantity)} 公克/毫升`;
}

/**
 * 數量的顯示格式：最多一位小數，整數不補 .0。
 *
 * 收斂浮點運算的誤差（如 82.30000000000001）；一位小數已超過估算本身的準確度。
 */
export function formatGrams(grams: number): string {
  return (Math.round(grams * 10) / 10).toLocaleString('zh-TW');
}
